// OutreachDrafts.jsx
// VA reviews AI-generated repeat customer outreach drafts
// Approve, edit, or dismiss before sending

import React, { useState, useEffect } from 'react';

const BASE_URL = process.env.REACT_APP_BASE_URL || window.location.origin;

const OutreachDrafts = () => {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('Pending Review');
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({ sms: '', subject: '', body: '' });
  const [sending, setSending] = useState(null);

  useEffect(() => { fetchDrafts(); }, []);

  const fetchDrafts = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/api/outreach`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'list' }),
      });
      const data = await res.json();
      setDrafts(data.drafts || []);
    } catch (e) {
      console.error('Failed to load drafts:', e);
    }
    setLoading(false);
  };

  const approveDraft = async (id) => {
    setSending(id);
    try {
      const res = await fetch(`${BASE_URL}/api/outreach`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'approve', draftId: id }),
      });
      const data = await res.json();
      if (data.success) {
        setDrafts(prev => prev.map(d => d.id === id ? { ...d, status: 'Sent', sent_at: new Date().toISOString() } : d));
      }
    } catch (e) {
      alert('Send failed: ' + e.message);
    }
    setSending(null);
  };

  const dismissDraft = async (id) => {
    try {
      await fetch(`${BASE_URL}/api/outreach`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'dismiss', draftId: id, reason: 'Dismissed by VA' }),
      });
      setDrafts(prev => prev.map(d => d.id === id ? { ...d, status: 'Dismissed' } : d));
    } catch (e) {
      alert('Dismiss failed: ' + e.message);
    }
  };

  const openEdit = (draft) => {
    setEditForm({ sms: draft.sms_draft || '', subject: draft.email_subject || '', body: draft.email_body || '' });
    setEditModal(draft);
  };

  const saveEdit = async () => {
    try {
      await fetch(`${BASE_URL}/api/outreach`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'edit',
          draftId: editModal.id,
          smsDraft: editForm.sms,
          emailSubject: editForm.subject,
          emailBody: editForm.body,
        }),
      });
      setDrafts(prev => prev.map(d =>
        d.id === editModal.id
          ? { ...d, sms_draft: editForm.sms, email_subject: editForm.subject, email_body: editForm.body }
          : d
      ));
      setEditModal(null);
    } catch (e) {
      alert('Save failed: ' + e.message);
    }
  };

  const filtered = filter === 'All' ? drafts : drafts.filter(d => d.status === filter);
  const pendingCount = drafts.filter(d => d.status === 'Pending Review').length;
  const sentCount = drafts.filter(d => d.status === 'Sent').length;

  return (
    <div className="fade-in" style={{ minHeight: 'calc(100vh - 120px)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 className="section-title">Customer Outreach</h1>
          <p className="section-sub">AI-generated outreach for repeat customers. Review, edit, and approve before sending.</p>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={fetchDrafts}>
          {loading ? 'Loading...' : '↻ Refresh'}
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        <div className="stat-card" style={{ flex: 1, padding: '14px 18px' }}>
          <div className="stat-value" style={{ fontSize: 24, color: 'var(--warm-gold)' }}>{pendingCount}</div>
          <div className="stat-label">Pending Review</div>
        </div>
        <div className="stat-card" style={{ flex: 1, padding: '14px 18px' }}>
          <div className="stat-value" style={{ fontSize: 24, color: 'var(--accent-green)' }}>{sentCount}</div>
          <div className="stat-label">Sent</div>
        </div>
        <div className="stat-card" style={{ flex: 1, padding: '14px 18px' }}>
          <div className="stat-value" style={{ fontSize: 24 }}>{drafts.length}</div>
          <div className="stat-label">Total Drafts</div>
        </div>
      </div>

      {/* Filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {['Pending Review', 'Sent', 'Dismissed', 'All'].map(f => (
          <button key={f} className="btn btn-sm" onClick={() => setFilter(f)} style={{
            background: filter === f ? 'var(--warm-gold)' : 'var(--stone-200)',
            color: filter === f ? 'white' : 'var(--stone-700)',
          }}>
            {f} {f === 'Pending Review' ? `(${pendingCount})` : ''}
          </button>
        ))}
      </div>

      {/* Drafts List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--stone-400)' }}>Loading drafts...</div>
      ) : filtered.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 60 }}>
          <div style={{ fontSize: 48, marginBottom: 12, opacity: 0.3 }}>📬</div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 600, color: 'var(--stone-400)', marginBottom: 6 }}>
            {filter === 'Pending Review' ? 'No Pending Outreach' : 'No Drafts Found'}
          </div>
          <div style={{ fontSize: 13, color: 'var(--stone-400)' }}>
            {filter === 'Pending Review' ? 'Outreach drafts are generated on the 1st of each month for customers with no activity in 6+ months.' : 'Try changing the filter.'}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filtered.map(d => (
            <div key={d.id} className="card" style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div className="avatar">{(d.customer_name || '??').split(' ').map(n => n[0]).join('')}</div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 15 }}>{d.customer_name}</div>
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      Last project: {d.last_project} · {d.months_since} months ago · {d.total_jobs} job{d.total_jobs !== 1 ? 's' : ''} · ${Number(d.total_revenue || 0).toLocaleString()} lifetime
                    </div>
                  </div>
                </div>
                <span className={`badge ${d.status === 'Sent' ? 'badge-won' : d.status === 'Dismissed' ? 'badge-lost' : 'badge-contacted'}`}>
                  {d.status}
                </span>
              </div>

              {d.suggested_project && (
                <div style={{ background: 'rgba(201,169,110,0.08)', border: '1px solid rgba(201,169,110,0.2)', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 12 }}>
                  <strong style={{ color: 'var(--warm-gold)' }}>Suggested project:</strong> {d.suggested_project}
                </div>
              )}

              {/* SMS Draft */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--stone-400)', textTransform: 'uppercase', marginBottom: 4 }}>SMS Draft</div>
                <div style={{ background: 'var(--stone-100)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--stone-600)', lineHeight: 1.6 }}>
                  {d.sms_draft || 'No SMS draft'}
                </div>
              </div>

              {/* Email Subject */}
              {d.email_subject && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--stone-400)', textTransform: 'uppercase', marginBottom: 4 }}>Email Subject</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--stone-700)' }}>{d.email_subject}</div>
                </div>
              )}

              {/* Actions */}
              {d.status === 'Pending Review' && (
                <div style={{ display: 'flex', gap: 8, marginTop: 14, borderTop: '1px solid var(--stone-200)', paddingTop: 14 }}>
                  <button className="btn btn-primary btn-sm" onClick={() => approveDraft(d.id)} disabled={sending === d.id}>
                    {sending === d.id ? 'Sending...' : '✓ Approve & Send'}
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => openEdit(d)}>✏️ Edit</button>
                  <button className="btn btn-ghost btn-sm" style={{ color: 'var(--accent-red)' }} onClick={() => dismissDraft(d.id)}>✕ Dismiss</button>
                </div>
              )}

              {d.sent_at && (
                <div style={{ fontSize: 11, color: 'var(--stone-400)', marginTop: 8 }}>
                  Sent on {new Date(d.sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      {editModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setEditModal(null)}>
          <div className="modal-box fade-in">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 600 }}>Edit Outreach — {editModal.customer_name}</h2>
              <button className="btn-icon" onClick={() => setEditModal(null)}>✕</button>
            </div>

            <div className="form-group">
              <label>SMS Message</label>
              <textarea rows={4} value={editForm.sms} onChange={e => setEditForm(p => ({ ...p, sms: e.target.value }))} />
              <div style={{ fontSize: 11, color: editForm.sms.length > 300 ? 'var(--accent-red)' : 'var(--stone-400)', marginTop: 4 }}>
                {editForm.sms.length}/300 characters
              </div>
            </div>

            <div className="form-group">
              <label>Email Subject</label>
              <input value={editForm.subject} onChange={e => setEditForm(p => ({ ...p, subject: e.target.value }))} />
            </div>

            <div className="form-group">
              <label>Email Body</label>
              <textarea rows={6} value={editForm.body} onChange={e => setEditForm(p => ({ ...p, body: e.target.value }))} />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button className="btn btn-secondary" onClick={() => setEditModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveEdit}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OutreachDrafts;
